import { Component, OnInit, ViewChild } from "@angular/core";
import { MatTableDataSource, MatPaginator, PageEvent } from "@angular/material";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { forkJoin, of } from "rxjs";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";
import { formatIndianNumber } from "src/_helpers/utils";
import {
  ARBITRAGE_DECISION_FAILURE_REASON_OPTIONS,
  ARBITRAGE_DECISION_NO_MARGIN_CASE_OPTIONS,
  ARBITRAGE_COMMON_MARKETS,
  ARBITRAGE_MARKET_NAMES_BY_SPORT,
} from "src/_constants/arbitrage-decisions";

interface DecisionBet {
  providerId: string;
  sportId: string;
  eventId: string;
  marketId: string;
  marketName: string;
  betType: string;
  runnerId: string;
  runnerName: string;
  odd: number;
  stake: number;
  runnerRisk?: number;
}

interface BFPreviousDecision {
  runnerId: string;
  runnerName: string;
  odd: number;
  stake: number;
  betType: string;
  margin: number;
  profit: number;
}

interface ArbitrageDecision {
  id?: string;
  bfBet: DecisionBet;
  srBet: DecisionBet;
  bfRetry?: boolean;
  bfPrevious?: BFPreviousDecision;
  eventName?: string;
  outcome: string;
  failureReason?: string;
  failureDetails?: string;
  margin?: number;
  bfVolume?: number;
  profit?: number;
  marketProfit?: number;
  marginAmount?: number;
  noMarginCase?: string;
  createdAt: number;
  updatedAt?: number;
}

interface EventDecisionSummary {
  eventId: string;
  eventName: string;
}

@Component({
  selector: "app-arbitrage-decisions",
  templateUrl: "./arbitrage-decisions.component.html",
  styleUrls: ["./arbitrage-decisions.component.scss"],
})
export class ArbitrageDecisionsComponent implements OnInit {
  selected: any = { startDate: null, endDate: null };
  startOfDay = moment().subtract(1, "days").startOf("day");
  endOfDay = moment().subtract(1, "days").endOf("day");
  ranges: any = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [moment(this.startOfDay), moment(this.endOfDay)],
    "Last 7 Days": [moment(moment().subtract(6, "days").startOf("day")), moment()],
    "Last 30 Days": [moment(moment().subtract(29, "days").startOf("day")), moment()],
    "This Month": [moment().startOf("month"), moment()],
    "Last Month": [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  };

  outcomeOptions: any[] = [
    { item_id: "SUCCESS", item_text: "SUCCESS" },
    { item_id: "FAILED", item_text: "FAILED" },
  ];
  failureReasonOptions = ARBITRAGE_DECISION_FAILURE_REASON_OPTIONS;
  noMarginCaseOptions = ARBITRAGE_DECISION_NO_MARGIN_CASE_OPTIONS;
  sportOptions: any[] = [];
  eventOptions: any[] = [];
  marketOptions: any[] = [];

  outcomeDropdownSettings: any = {};
  failureReasonDropdownSettings: any = {};
  noMarginCaseDropdownSettings: any = {};
  sportDropdownSettings: any = {};
  eventDropdownSettings: any = {};
  marketDropdownSettings: any = {};

  selectedOutcomeDropdown = new FormControl([]);
  selectedFailureReasonDropdown = new FormControl([]);
  selectedNoMarginCaseDropdown = new FormControl([]);
  selectedSportDropdown = new FormControl([]);
  selectedEventDropdown = new FormControl([]);
  selectedMarketDropdown = new FormControl([]);

  selectedOutcome: string = "";
  selectedFailureReasons: string[] = [];
  selectedNoMarginCases: string[] = [];
  selectedSportIds: string[] = [];
  selectedEventIds: string[] = [];
  selectedMarketNames: string[] = [];
  sportNameById: { [key: string]: string } = {};

  decisionsList = new MatTableDataSource<ArbitrageDecision>([]);
  displayedColumns: string[] = [
    "createdAt",
    "sportName",
    "eventName",
    "bfMarketName",
    "margin",
    "noMarginCase",
    "outcome",
    "bfRetry",
    "bfVolume",
    "profit",
    "marketProfit",
    "failureReason",
    "failureDetails",
  ];
  loading: boolean = false;
  expandedElement: ArbitrageDecision | null = null;
  selectedBfPrevious: BFPreviousDecision | null = null;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  page = 1;
  pageSize = 25;
  orgPageSize = 25;
  length = 0;
  pageEvent: PageEvent;

  constructor(private appService: AppService, private router: Router) { }

  ngOnInit() {
    this.selected = {
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
    };
    this.initializeDropdownSettings();
    this.loadSportOptions();
  }

  initializeDropdownSettings() {
    this.outcomeDropdownSettings = {
      singleSelection: true,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.failureReasonDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.noMarginCaseDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.sportDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 2,
      allowSearchFilter: true,
    };
    this.eventDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
    this.marketDropdownSettings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "UnSelect All",
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
  }

  private buildMarketOptions(sportId: string): any[] {
    const sid = String(sportId || "").trim();
    const list = (ARBITRAGE_MARKET_NAMES_BY_SPORT[sid] || [ARBITRAGE_COMMON_MARKETS.matchOdds]).slice();
    const unique = Array.from(new Set(list.length ? list : [ARBITRAGE_COMMON_MARKETS.matchOdds]));
    return unique.map((name) => ({ item_id: name, item_text: name }));
  }

  private refreshMarketOptions() {
    // Only show market list when exactly one sport is selected.
    if (this.selectedSportIds.length !== 1) {
      this.marketOptions = [];
      this.selectedMarketNames = [];
      this.selectedMarketDropdown.setValue([]);
      return;
    }

    const sportIdForMarkets = this.selectedSportIds[0];
    this.marketOptions = this.buildMarketOptions(sportIdForMarkets);
    this.selectedMarketNames = [];
    this.selectedMarketDropdown.setValue([]);
  }

  onDateChangeHandler(event: any) {
    this.selected = event;
    if (this.selectedSportIds.length === 1) {
      this.loadEventOptions(false);
    } else {
      this.eventOptions = [];
      this.selectedEventIds = [];
      this.selectedEventDropdown.setValue([]);
    }
  }

  getDateRangeFilters() {
    return {
      startTime: this.selected.startDate
        ? Math.floor(new Date(this.selected.startDate).getTime() / 1000)
        : 0,
      endTime: this.selected.endDate
        ? Math.floor(new Date(this.selected.endDate).getTime() / 1000)
        : 0,
    };
  }

  loadSportOptions() {
    this.loading = true;
    this.appService.getSportForBetList("BetFair").subscribe(
      (res) => {
        if (res.status === "RS_OK") {
          const data: any[] = res.sports || [];
          const mapById = new Map<string, string>();
          this.sportNameById = {};
          (data || []).forEach((item) => {
            const sportId = item && item.SportId ? item.SportId : "";
            const sportName = item && item.SportName ? item.SportName : sportId;
            if (sportId && sportId !== "All") {
              mapById.set(sportId, sportName);
              this.sportNameById[sportId] = sportName;
            }
          });

          this.sportOptions = Array.from(mapById.entries()).map(([sportId, sportName]) => ({
            item_id: sportId,
            item_text: sportName,
          }));

          const validSportIds = new Set(this.sportOptions.map((item) => item.item_id));
          this.selectedSportIds = this.selectedSportIds.filter((id) => validSportIds.has(id));
          this.selectedSportDropdown.setValue(
            this.sportOptions.filter((item) => this.selectedSportIds.includes(item.item_id))
          );

          this.refreshMarketOptions();
          this.loadEventOptions(false);
        } else {
          this.loading = false;
          this.appService.openErrorBar(res.errorDescription || "Failed to fetch sports");
        }
      },
      (error) => {
        this.loading = false;
        const errorMessage =
          error.error && error.error.errorDescription
            ? error.error.errorDescription
            : "Error fetching sports";
        this.appService.openErrorBar(errorMessage);
      }
    );
  }

  loadEventOptions(loadDecisionsAfterLoad: boolean = false) {
    const dateFilters = this.getDateRangeFilters();
    if (!dateFilters.startTime || !dateFilters.endTime) {
      this.loading = false;
      return;
    }

    if (this.selectedSportIds.length !== 1) {
      this.eventOptions = [];
      this.selectedEventIds = [];
      this.selectedEventDropdown.setValue([]);
      this.loading = false;
      if (loadDecisionsAfterLoad) {
        this.getDecisions();
      }
      return;
    }

    const requests = this.selectedSportIds.map((sportId) =>
      this.appService.getEventLevelArbitrageDecisions({
        sportId: sportId,
        startTime: dateFilters.startTime,
        endTime: dateFilters.endTime,
      })
    );

    forkJoin(requests.length ? requests : [of({ status: "RS_OK", data: [] })]).subscribe(
      (responses: any[]) => {
        const eventMap = new Map<string, string>();
        (responses || []).forEach((res) => {
          if (res && res.status === "RS_OK") {
            const events: EventDecisionSummary[] = res.data || res.eventDecisions || res.decisions || [];
            (events || []).forEach((eventItem) => {
              if (eventItem && eventItem.eventId) {
                eventMap.set(eventItem.eventId, eventItem.eventName || eventItem.eventId);
              }
            });
          }
        });

        this.eventOptions = Array.from(eventMap.entries()).map(([eventId, eventName]) => ({
          item_id: eventId,
          item_text: eventName,
        }));

        const validEventIds = new Set(this.eventOptions.map((item) => item.item_id));
        this.selectedEventIds = this.selectedEventIds.filter((id) => validEventIds.has(id));
        this.selectedEventDropdown.setValue(
          this.eventOptions.filter((item) => this.selectedEventIds.includes(item.item_id))
        );

        this.loading = false;
        if (loadDecisionsAfterLoad) {
          this.getDecisions();
        }
      },
      (error) => {
        this.loading = false;
        const errorMessage =
          error.error && error.error.errorDescription
            ? error.error.errorDescription
            : "Error fetching events";
        this.appService.openErrorBar(errorMessage);
      }
    );
  }

  onSportFilterChange() {
    this.selectedSportIds = (this.selectedSportDropdown.value || [])
      .map((item) => (item && item.item_id ? item.item_id : ""))
      .filter((value) => !!value);
    this.refreshMarketOptions();
    if (this.selectedSportIds.length === 1) {
      this.loadEventOptions(false);
      return;
    }
    this.eventOptions = [];
    this.selectedEventIds = [];
    this.selectedEventDropdown.setValue([]);
  }

  onSportFilterSelectAll(items: any[]) {
    this.selectedSportIds = (items || [])
      .map((item) => (item && item.item_id ? item.item_id : ""))
      .filter((value) => !!value);
    this.refreshMarketOptions();
    if (this.selectedSportIds.length === 1) {
      this.loadEventOptions(false);
      return;
    }
    this.eventOptions = [];
    this.selectedEventIds = [];
    this.selectedEventDropdown.setValue([]);
  }

  clearSportFilter() {
    this.selectedSportIds = [];
    this.refreshMarketOptions();
    this.loadEventOptions(false);
  }

  onEventFilterChange() {
    this.selectedEventIds = (this.selectedEventDropdown.value || [])
      .map((item) => (item && item.item_id ? item.item_id : ""))
      .filter((value) => !!value);
  }

  onEventFilterSelectAll(items: any[]) {
    this.selectedEventIds = (items || [])
      .map((item) => (item && item.item_id ? item.item_id : ""))
      .filter((value) => !!value);
  }

  clearEventFilter() {
    this.selectedEventIds = [];
  }

  getDecisions = () => {
    const dateFilters = this.getDateRangeFilters();
    if (!dateFilters.startTime || !dateFilters.endTime) {
      this.appService.openErrorBar("Please select both start and end dates");
      return;
    }

    this.loading = true;
    const filters: any = {
      startTime: dateFilters.startTime,
      endTime: dateFilters.endTime,
      page: this.page,
      pageSize: this.orgPageSize,
      sportIds: this.selectedSportIds,
    };

    if ((this.selectedSportIds.length === 1) && (this.selectedEventIds.length)) {
      filters.eventIds = this.selectedEventIds;
    }
    if (this.selectedOutcome) {
      filters.outcome = this.selectedOutcome;
    }
    if (this.selectedFailureReasons.length) {
      filters.failureReasons = this.selectedFailureReasons;
    }
    if (this.selectedNoMarginCases.length) {
      filters.noMarginCases = this.selectedNoMarginCases;
    }
    if (this.selectedMarketNames.length) {
      filters.marketNames = this.selectedMarketNames;
    }

    this.appService.getArbitrageDecisions(filters).subscribe(
      (res) => {
        this.loading = false;
        if (res.status === "RS_OK") {
          const data: ArbitrageDecision[] = res.decisions || res.data || [];
          this.decisionsList = new MatTableDataSource<ArbitrageDecision>(data);
          if (this.page === 1) {
            this.length = res.totalRecords || 0;
          } else if (this.paginator) {
            this.paginator.length = this.length;
          }
        } else if (res.errorDescription === "Invalid Token!") {
          this.appService.openErrorBar(res.errorDescription);
          sessionStorage.clear();
          this.router.navigateByUrl("/login");
        } else {
          this.appService.openErrorBar(res.errorDescription || "Failed to fetch decisions");
        }
      },
      (error) => {
        this.loading = false;
        const errorMessage =
          error.error && error.error.errorDescription
            ? error.error.errorDescription
            : "Error fetching decisions";
        this.appService.openErrorBar(errorMessage);
      }
    );
  };

  changePagination(event: PageEvent) {
    this.pageEvent = event;
    this.page = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.orgPageSize = event.pageSize;
    this.getDecisions();
  }

  refreshDecisions() {
    if (this.loading) {
      return;
    }
    this.loadEventOptions(true);
  }

  onOutcomeFilterChange(item: any) {
    this.selectedOutcome = item && item.item_id ? item.item_id : "";
    if (this.selectedOutcome === "SUCCESS") {
      this.selectedFailureReasons = [];
      this.selectedFailureReasonDropdown.setValue([]);
    }
  }

  clearOutcomeFilter() {
    this.selectedOutcome = "";
  }

  onFailureReasonFilterChange(item: any) {
    const selected = item && item.item_id ? item.item_id : "";
    if (selected && !this.selectedFailureReasons.includes(selected)) {
      this.selectedFailureReasons.push(selected);
    }
  }

  onFailureReasonFilterDeselect(item: any) {
    const deselected = item && item.item_id ? item.item_id : "";
    this.selectedFailureReasons = this.selectedFailureReasons.filter((reason) => reason !== deselected);
  }

  onFailureReasonFilterSelectAll(items: any[]) {
    this.selectedFailureReasons = (items || [])
      .map((item) => item && item.item_id)
      .filter((value) => !!value);
  }

  clearFailureReasonFilter() {
    this.selectedFailureReasons = [];
  }

  onNoMarginCaseFilterChange(item: any) {
    const selected = item && item.item_id ? item.item_id : "";
    if (selected && !this.selectedNoMarginCases.includes(selected)) {
      this.selectedNoMarginCases.push(selected);
    }
  }

  onNoMarginCaseFilterDeselect(item: any) {
    const deselected = item && item.item_id ? item.item_id : "";
    this.selectedNoMarginCases = this.selectedNoMarginCases.filter((value) => value !== deselected);
  }

  onNoMarginCaseFilterSelectAll(items: any[]) {
    this.selectedNoMarginCases = (items || [])
      .map((item) => item && item.item_id)
      .filter((value) => !!value);
  }

  clearNoMarginCaseFilter() {
    this.selectedNoMarginCases = [];
  }

  onMarketFilterChange(item: any) {
    const selected = item && item.item_id ? item.item_id : "";
    if (selected && !this.selectedMarketNames.includes(selected)) {
      this.selectedMarketNames.push(selected);
    }
  }

  onMarketFilterDeselect(item: any) {
    const deselected = item && item.item_id ? item.item_id : "";
    this.selectedMarketNames = this.selectedMarketNames.filter((value) => value !== deselected);
  }

  onMarketFilterSelectAll(items: any[]) {
    this.selectedMarketNames = (items || [])
      .map((i) => (i && i.item_id ? i.item_id : ""))
      .filter((v) => !!v);
  }

  clearMarketFilter() {
    this.selectedMarketNames = [];
    this.selectedMarketDropdown.setValue([]);
  }

  applyDecisionFilters() {
    this.page = 1;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.loadEventOptions(true);
  }

  toggleRow(element: ArbitrageDecision) {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  isExpanded(element: ArbitrageDecision): boolean {
    return this.expandedElement === element;
  }

  formatCreatedAt(createdAt: number): string {
    if (!createdAt) {
      return "";
    }
    return moment(createdAt).format("DD-MM-YYYY HH:mm:ss.SSS");
  }

  getDecisionSportName(element: ArbitrageDecision): string {
    if (!element) {
      return "-";
    }
    const sportId =
      (element.bfBet && element.bfBet.sportId) ||
      (element.srBet && element.srBet.sportId) ||
      "";
    if (!sportId) {
      return "-";
    }
    return this.sportNameById[sportId] || sportId;
  }

  formatFixed(value: any, precision: number = 2): string {
    return formatIndianNumber(value, precision);
  }

  openBfPreviousPopup(element: ArbitrageDecision, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (element && element.bfRetry && element.bfPrevious) {
      this.selectedBfPrevious = element.bfPrevious;
    }
  }

  closeBfPreviousPopup(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.selectedBfPrevious = null;
  }
}
